/**
 * The colour scheme: a choice kept in a cookie for all of ogreclan.com (so replay.ogreclan.com matches), shown as
 * <html data-theme> and styled by static/theme.css. index.html sets it before the page is drawn.
 */
import { useEffect, useState } from 'preact/hooks';

/** Plain colours, then OCDL's divisions, whose artwork is the page (always dark, like the pictures) */
export const THEMES = [
	{ id: 'device', name: 'Device (light or dark)', division: false },
	{ id: 'light', name: 'Light', division: false },
	{ id: 'dark', name: 'Dark', division: false },
	{ id: 'teal', name: 'Teal', division: true },
	{ id: 'wellspring', name: 'Wellspring', division: true },
	{ id: 'hearthflame', name: 'Hearthflame', division: true },
	{ id: 'cornerstone', name: 'Cornerstone', division: true },
] as const;
export type Theme = typeof THEMES[number]['id'];

const COOKIE = 'ogreclan_theme';
const darkDevice = window.matchMedia('(prefers-color-scheme: dark)');
const listeners = new Set<() => void>();

function isTheme(value: unknown): value is Theme {
	return THEMES.some(theme => theme.id === value);
}

export function currentTheme(): Theme {
	const saved = new RegExp(`(?:^|; )${COOKIE}=([a-z]+)`).exec(document.cookie)?.[1];
	return isTheme(saved) ? saved : 'device';
}

/** What the page looks like now: the division (or none) and whether it's dark */
export function appearance(theme = currentTheme()) {
	const scheme = THEMES.find(t => t.id === theme)?.division ? theme : null;
	const dark = theme === 'dark' || scheme !== null || (theme !== 'light' && darkDevice.matches);
	return { theme, scheme, dark };
}

/** The cookie is for the whole site (draft. and replay.ogreclan.com), not just this address */
function cookieDomain() {
	const parts = location.hostname.split('.');
	return parts.length > 2 && !/^\d+$/.test(parts[parts.length - 1]!) ? `; Domain=${parts.slice(-2).join('.')}` : '';
}

export function setTheme(theme: Theme) {
	const secure = location.protocol === 'https:' ? '; Secure' : '';
	document.cookie = `${COOKIE}=${theme}; Path=/; Max-Age=${60 * 60 * 24 * 400}; SameSite=Lax${cookieDomain()}${secure}`;
	document.documentElement.setAttribute('data-theme', theme);
	for (const listener of listeners) listener();
}

darkDevice.addEventListener('change', () => {
	for (const listener of listeners) listener();
});

/** Calls back when the appearance changes; returns a function that stops it */
export function watchAppearance(callback: (now: ReturnType<typeof appearance>) => void) {
	const listener = () => callback(appearance());
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

/** The page's appearance, kept up to date when it's changed or the device switches between light and dark */
export function useAppearance() {
	const [value, setValue] = useState(appearance);
	useEffect(() => {
		const update = () => setValue(appearance());
		listeners.add(update);
		return () => {
			listeners.delete(update);
		};
	}, []);
	return value;
}

export function ThemePicker() {
	const { theme } = useAppearance();
	return <label class="theme-picker" title="Colour scheme">
		<span class="sr-only">Colour scheme</span>
		<select value={theme} onChange={e => setTheme((e.target as HTMLSelectElement).value as Theme)}>
			{THEMES.filter(t => !t.division).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
			<optgroup label="Divisions">
				{THEMES.filter(t => t.division).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
			</optgroup>
		</select>
	</label>;
}
